import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  Typography,
  Box,
  Paper,
  Chip,
  IconButton,
  Tooltip,
} from "@mui/material";
import { QRCodeSVG } from "qrcode.react";
import { useTranslation } from "react-i18next";
import ESIMStatusChip from "./ESIMStatusChip";
import {
  Apple as AppleIcon,
  ContentCopy as ContentCopyIcon,
} from "@mui/icons-material";

const ESIMOrderDetails = ({ order, open, onClose }) => {
  const { t } = useTranslation();
  const [copied, setCopied] = useState(false);

  if (!order) return null;

  // Construct Apple setup URL from activation code
  const getAppleSetupUrl = () => {
    if (order.activationCode) {
      // The activation code starts with "LPA:1$rsp-..."
      return `https://esimsetup.apple.com/esim_qrcode_provisioning?carddata=${order.activationCode}`;
    }
    return null;
  };

  const formatDataUsage = (used, total) => {
    const usedGB = (used / (1024 * 1024 * 1024)).toFixed(2);
    const totalGB = (total / (1024 * 1024 * 1024)).toFixed(2);
    return `${usedGB}GB / ${totalGB}GB`;
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        {t("esim.orderDetails.title")}
        <Typography variant="subtitle2" color="textSecondary">
          {order.orderNo}
        </Typography>
      </DialogTitle>

      <DialogContent>
        <Grid container spacing={3}>
          {/* Status Section */}
          <Grid item xs={12}>
            <Paper sx={{ p: 2, mb: 2 }}>
              <Typography variant="h6" gutterBottom>
                {t("esim.orderDetails.status")}
              </Typography>
              <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                <ESIMStatusChip status={order.status} />
                {order.smdpStatus && (
                  <Chip
                    label={t(`esim.orders.smdpStatus.${order.smdpStatus}`)}
                    variant="outlined"
                  />
                )}
              </Box>
            </Paper>
          </Grid>

          {/* Customer Details Section */}
          <Grid item xs={12}>
            <Paper sx={{ p: 2 }}>
              <Typography variant="h6" gutterBottom>
                {t("esim.orderDetails.customerDetails")}
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2">
                    {t("esim.orderDetails.customerName")}
                  </Typography>
                  <Typography>{order.customerName}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2">
                    {t("esim.orderDetails.customerEmail")}
                  </Typography>
                  <Typography>{order.customerEmail}</Typography>
                </Grid>
              </Grid>
            </Paper>
          </Grid>

          {/* QR Code Section */}
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 2, height: "100%" }}>
              <Typography variant="h6" gutterBottom>
                {t("esim.orderDetails.qrCode")}
              </Typography>
              <Box sx={{ textAlign: "center", my: 2 }}>
                {order.activationCode && (
                  <QRCodeSVG value={order.activationCode} size={200} />
                )}
                <Typography variant="caption" display="block" mt={1}>
                  {t("esim.orderDetails.scanQrCode")}
                </Typography>
              </Box>
            </Paper>
          </Grid>

          {/* Activation Details */}
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 2, height: "100%" }}>
              <Typography variant="h6" gutterBottom>
                {t("esim.orderDetails.activationDetails")}
              </Typography>
              <Grid container spacing={2}>
                {order.activationCode && (
                  <Grid item xs={12}>
                    <Typography variant="subtitle2">
                      {t("esim.details.activationCode")}
                    </Typography>
                    <Box
                      sx={{
                        position: "relative",
                        backgroundColor: "#3b3737",
                        borderRadius: 1,
                        p: 1.5,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        wordBreak: "break-all",
                        fontFamily: "monospace",
                      }}
                    >
                      <Typography
                        sx={{ fontSize: "16px", fontWeight: "bold", mr: 1 }}
                      >
                        {order.activationCode}
                      </Typography>
                      <Tooltip title={copied ? "Copied!" : "Copy code"}>
                        <IconButton
                          onClick={() => {
                            navigator.clipboard.writeText(order.activationCode);
                            setCopied(true);
                            setTimeout(() => setCopied(false), 2000);
                          }}
                          size="small"
                        >
                          <ContentCopyIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </Grid>
                )}
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2">ICCID</Typography>
                  <Typography>{order.iccid || "-"}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2">IMSI</Typography>
                  <Typography>{order.imsi || "-"}</Typography>
                </Grid>
                {/* Apple Quick Install Button */}
                {order.activationCode && (
                  <Grid item xs={12}>
                    <Box
                      sx={{
                        mt: 2,
                        p: 2,
                        border: "1px solid rgba(255,255,255,0.1)",
                        borderRadius: 1,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        backgroundColor: "rgba(255,255,255,0.05)",
                      }}
                      onClick={() => window.open(getAppleSetupUrl(), "_blank")}
                      style={{ cursor: "pointer" }}
                    >
                      <Typography
                        variant="body1"
                        sx={{ display: "flex", alignItems: "center" }}
                      >
                        <AppleIcon sx={{ mr: 1 }} />
                        {t("esim.orderDetails.appleQuickInstall")}
                      </Typography>
                    </Box>
                  </Grid>
                )}
              </Grid>
            </Paper>
          </Grid>

          {/* Package Details */}
          <Grid item xs={12}>
            <Paper sx={{ p: 2 }}>
              <Typography variant="h6" gutterBottom>
                {t("esim.orderDetails.packageDetails")}
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2">
                    {t("esim.orderDetails.packageName")}
                  </Typography>
                  <Typography>{order.package?.name}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2">
                    {t("esim.orderDetails.dataUsage")}
                  </Typography>
                  <Typography>
                    {formatDataUsage(order.orderUsage || 0, order.totalVolume)}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2">
                    {t("esim.orderDetails.validity")}
                  </Typography>
                  <Typography>{`${order.duration} ${order.durationUnit}`}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2">
                    {t("esim.orderDetails.expiryDate")}
                  </Typography>
                  <Typography>
                    {new Date(order.expiryDate).toLocaleDateString()}
                    {order.remainingDays !== undefined &&
                      ` (${t("esim.orderDetails.daysRemaining", {
                        days: order.remainingDays,
                      })})`}
                  </Typography>
                </Grid>
              </Grid>
            </Paper>
          </Grid>

          {/* Notes Section - if present */}
          {order.notes && (
            <Grid item xs={12}>
              <Paper sx={{ p: 2 }}>
                <Typography variant="h6" gutterBottom>
                  {t("esim.orderDetails.notes")}
                </Typography>
                <Typography variant="body1">{order.notes}</Typography>
              </Paper>
            </Grid>
          )}
        </Grid>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>{t("common.actions.close")}</Button>
        {order.qrCodeUrl && (
          <Button
            variant="contained"
            color="primary"
            onClick={() => window.open(order.qrCodeUrl, "_blank")}
          >
            {t("common.actions.downloadQR")}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default ESIMOrderDetails;
